/**
 * One thing on the calendar. Colour says what it is: tasks take their
 * traffic source's colour, live calls are flame, booked calls (booking
 * links) indigo, events amber, goal dates violet with a flag, affiliate moves
 * emerald. A faint dashed one is a routine's next repeat.
 */
import { Link, useLocation } from "react-router-dom";
import { Bot, CalendarCheck, Check, Flag, Handshake, Link2, Repeat, Video } from "lucide-react";
import { cn } from "@/lib/utils";
import { dotClass, firstName } from "@/features/command/ui";
import { timeLabel } from "../lib";
import type { CalItem } from "../types";

const BAR: Record<string, string> = {
  pink: "border-l-pink-400", violet: "border-l-violet-400", blue: "border-l-sky-400", amber: "border-l-amber-400", zinc: "border-l-zinc-500",
  emerald: "border-l-emerald-400", orange: "border-l-orange-400", indigo: "border-l-indigo-400", sky: "border-l-sky-400", fuchsia: "border-l-fuchsia-400",
  teal: "border-l-teal-400", rose: "border-l-rose-400", yellow: "border-l-yellow-400", stone: "border-l-stone-400", lime: "border-l-lime-400",
  slate: "border-l-slate-400", magenta: "border-l-fuchsia-400", green: "border-l-emerald-400", red: "border-l-red-400",
};
export const barClass = (c: string | null | undefined) => BAR[String(c ?? "")] ?? "border-l-zinc-600";

export function duration(min: number | null | undefined): string {
  if (!min) return "";
  if (min >= 1440) return "all day";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60), m = min % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

/** Dot colour for the compact (month) and legend views. */
export function itemDot(it: CalItem): string {
  if (it.type === "live_call") return "bg-rose-400";
  if (it.type === "appointment") return "bg-indigo-400";
  if (it.type === "event") return "bg-amber-400";
  if (it.type === "goal") return "bg-violet-400";
  if (it.type === "move") return it.link?.startsWith("/partners") ? "bg-sky-400" : "bg-emerald-400";
  return it.color ? dotClass(it.color) : "bg-zinc-400";
}

export function CalChip({
  it, onOpen, showOwner, compact,
}: { it: CalItem; onOpen: (it: CalItem) => void; showOwner?: boolean; compact?: boolean }) {
  const { pathname } = useLocation();
  const time = it.start ? timeLabel(it.start) : "";

  if (compact) {
    const body = (
      <>
        <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", itemDot(it), it.ghost && "opacity-50")} />
        {time && <span className="shrink-0 tabular-nums text-zinc-500">{time.replace(" ", "")}</span>}
        <span className={cn("truncate", it.done ? "text-zinc-500 line-through" : it.ghost ? "text-zinc-500" : "text-zinc-200")}>{it.title}</span>
      </>
    );
    const cls = "flex w-full min-w-0 items-center gap-1 rounded px-1 py-px text-left text-[11px] hover:bg-zinc-800/60";
    if (it.type === "move" && it.link) return <Link to={it.link} state={{ from: pathname }} onClick={(e) => e.stopPropagation()} className={cls}>{body}</Link>;
    return <button type="button" onClick={(e) => { e.stopPropagation(); onOpen(it); }} className={cls}>{body}</button>;
  }

  if (it.type === "live_call") {
    return (
      <div className="rounded-lg bg-rose-500/10 px-2 py-1.5 ring-1 ring-inset ring-rose-400/25">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-rose-200">
          <Video className="h-3 w-3 shrink-0" />
          <span className="tabular-nums">{time}</span>
          <span className="truncate font-medium">{it.title}</span>
        </div>
        <div className="mt-1 flex items-center justify-between gap-1">
          <span className="text-[10px] text-rose-200/60">{duration(it.minutes)}</span>
          {it.link && (
            <a href={it.link} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="rounded-md cc-flame-bg px-2 py-0.5 text-[10px] font-semibold text-white shadow-sm hover:opacity-90">
              Join
            </a>
          )}
        </div>
      </div>
    );
  }

  if (it.type === "appointment") {
    // Someone booked a call on a booking link: open their contact; Join opens the Meet link.
    return (
      <div role="button" tabIndex={0} onClick={() => onOpen(it)} onKeyDown={(e) => { if (e.key === "Enter") onOpen(it); }}
        className="cursor-pointer rounded-lg bg-indigo-500/10 px-2 py-1.5 ring-1 ring-inset ring-indigo-400/25 hover:bg-indigo-500/15">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-indigo-200">
          <CalendarCheck className="h-3 w-3 shrink-0" />
          <span className="tabular-nums">{time}</span>
          <span className="truncate font-medium text-indigo-100">{it.title}</span>
        </div>
        <div className="mt-1 flex items-center justify-between gap-1">
          <span className="text-[10px] text-indigo-200/60">{duration(it.minutes)}</span>
          {it.link && (
            <a href={it.link} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="rounded-md bg-indigo-500 px-2 py-0.5 text-[10px] font-semibold text-white shadow-sm hover:bg-indigo-400">
              Join
            </a>
          )}
        </div>
      </div>
    );
  }

  if (it.type === "event") {
    return (
      <button type="button" onClick={() => onOpen(it)} className="block w-full rounded-lg bg-amber-500/10 px-2 py-1.5 text-left ring-1 ring-inset ring-amber-400/25 hover:bg-amber-500/15">
        <div className="flex items-center gap-1.5 text-[11px] text-amber-200">
          {time && <span className="shrink-0 font-semibold tabular-nums">{time}</span>}
          <span className="truncate font-medium text-amber-100">{it.title}</span>
        </div>
        <div className="mt-0.5 flex items-center gap-1.5 text-[10px] text-amber-200/60">
          {duration(it.minutes)}
          {it.link && <Link2 className="h-3 w-3" />}
          {showOwner && it.owner && <span className="ml-auto truncate">{firstName(it.owner)}</span>}
        </div>
      </button>
    );
  }

  if (it.type === "goal") {
    return (
      <button type="button" onClick={() => onOpen(it)} className="flex w-full items-start gap-1.5 rounded-lg bg-violet-500/10 px-2 py-1.5 text-left text-[11px] ring-1 ring-inset ring-violet-400/25 hover:bg-violet-500/15">
        <Flag className="mt-px h-3 w-3 shrink-0 text-violet-300" />
        <span className="min-w-0">
          <span className="block text-[10px] font-semibold uppercase tracking-wider text-violet-300/80">Goal date</span>
          <span className="line-clamp-2 font-medium text-violet-100">{it.title}</span>
        </span>
      </button>
    );
  }

  if (it.type === "move") {
    // Affiliate moves are green, partner touches and promo dates sky blue.
    const partner = it.link?.startsWith("/partners");
    return (
      <Link to={it.link ?? "/affiliates"} state={{ from: pathname }} className={cn("flex items-start gap-1.5 rounded-lg px-2 py-1.5 text-[11px] ring-1 ring-inset", partner ? "bg-sky-500/10 ring-sky-400/25 hover:bg-sky-500/15" : "bg-emerald-500/10 ring-emerald-400/25 hover:bg-emerald-500/15")}>
        <Handshake className={cn("mt-px h-3 w-3 shrink-0", partner ? "text-sky-300" : "text-emerald-300")} />
        <span className={cn("line-clamp-2 font-medium", partner ? "text-sky-100" : "text-emerald-100")}>{it.title}</span>
      </Link>
    );
  }

  // Task (or a routine's next repeat).
  return (
    <button
      type="button"
      onClick={() => onOpen(it)}
      className={cn(
        "block w-full rounded-lg border-l-2 bg-zinc-900/70 px-2 py-1.5 text-left ring-1 ring-inset ring-zinc-800 transition-colors hover:bg-zinc-800/70",
        barClass(it.color),
        it.ghost && "border-dashed bg-transparent opacity-55 hover:opacity-100",
      )}
    >
      <div className="flex items-start gap-1.5">
        {it.done && <Check className="mt-px h-3 w-3 shrink-0 text-emerald-400" strokeWidth={3} />}
        <span className={cn("line-clamp-2 text-[11px] font-medium leading-snug", it.done ? "text-zinc-500 line-through" : "text-zinc-100")}>{it.title}</span>
      </div>
      <div className="mt-0.5 flex items-center gap-1.5 text-[10px] text-zinc-500">
        {time && <span className="tabular-nums text-zinc-400">{time}</span>}
        {it.repeat && <Repeat className="h-2.5 w-2.5" aria-label="Repeats" />}
        {it.ai && <Bot className="h-2.5 w-2.5 text-indigo-300" aria-label="An AI employee does it" />}
        {it.source_name && <span className="truncate">{it.source_name}</span>}
        {showOwner && it.owner && <span className="ml-auto shrink-0">{firstName(it.owner)}</span>}
      </div>
    </button>
  );
}

export function Legend() {
  const items = [
    { label: "Tasks", dot: "bg-zinc-400" },
    { label: "Live calls", dot: "bg-rose-400" },
    { label: "Booked calls", dot: "bg-indigo-400" },
    { label: "Events", dot: "bg-amber-400" },
    { label: "Goal dates", dot: "bg-violet-400" },
    { label: "Affiliate moves", dot: "bg-emerald-400" },
  ];
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-zinc-500">
      {items.map((i) => <span key={i.label} className="inline-flex items-center gap-1.5"><span className={cn("h-2 w-2 rounded-full", i.dot)} />{i.label}</span>)}
      <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-3 rounded-sm border border-dashed border-zinc-500" />Next repeats</span>
    </div>
  );
}
